// PDF documents for the source viewer: PDF.js loaded on demand in its own chunk with one shared module worker, each document opened from its first 256 KiB range (as data when that range holds the whole file, otherwise through a range transport that fetches further 256 KiB chunks) with the session cookie (apiFetch, retried on network and 5xx failures), shared by every viewer of the same version, kept open for a minute after the last one unmounts, with loading and error states for React.
import type * as PdfJsModule from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { useCallback, useEffect, useSyncExternalStore } from "react";

import { apiFetch } from "@/api/client";
import { ApiProblem, isApiProblem } from "@/api/problem";
import { encodeUid } from "@/lib/url";

export const RANGE_CHUNK_BYTES = 262_144;
const RELEASE_AFTER_MS = 60_000;
const RETRY_DELAYS_MS = [300, 1200] as const;
const MAX_OPEN_DOCUMENTS = 3;
const CONTENT_RANGE = /^bytes\s+\d+-\d+\/(\d+)$/iu;

export const PDFJS_ASSETS = {
  cMapUrl: "/pdfjs/cmaps/",
  cMapPacked: true,
  standardFontDataUrl: "/pdfjs/standard_fonts/",
  wasmUrl: "/pdfjs/wasm/",
  iccUrl: "/pdfjs/iccs/",
} as const;

export type PdfJs = typeof PdfJsModule;

export interface PdfDocumentSnapshot {
  status: "loading" | "ready" | "error";
  doc: PDFDocumentProxy | null;
  error: unknown;
}

export interface PdfDocumentState extends PdfDocumentSnapshot {
  retry: () => void;
}

interface Entry {
  snapshot: PdfDocumentSnapshot;
  refs: number;
  timer: ReturnType<typeof setTimeout> | null;
  destroy: () => void;
  generation: number;
}

const LOADING: PdfDocumentSnapshot = { status: "loading", doc: null, error: null };
const entries = new Map<string, Entry>();
const listeners = new Map<string, Set<() => void>>();
let pdfjsPromise: Promise<PdfJs> | null = null;
let generations = 0;

export function pdfPath(docUid: string): string {
  return `/api/v1/documents/${encodeUid(docUid)}/pdf`;
}

export function loadPdfJs(): Promise<PdfJs> {
  pdfjsPromise ??= Promise.all([import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?url")])
    .then(([pdfjs, worker]) => {
      pdfjs.GlobalWorkerOptions.workerPort = new Worker(worker.default, { type: "module" });
      return pdfjs;
    })
    .catch((error: unknown) => {
      pdfjsPromise = null;
      throw error;
    });
  return pdfjsPromise;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface RangeBytes {
  bytes: Uint8Array;
  total: number;
  partial: boolean;
}

export function totalFromContentRange(header: string | null): number | null {
  const match = header === null ? null : CONTENT_RANGE.exec(header.trim());
  return match?.[1] === undefined ? null : Number(match[1]);
}

export async function fetchRange(path: string, begin: number, end: number): Promise<RangeBytes> {
  for (let attempt = 0; ; attempt += 1) {
    let problem: ApiProblem;
    try {
      const response = await apiFetch(path, { headers: { Range: `bytes=${begin}-${end - 1}` } });
      if (response.status === 206 || response.status === 200) {
        const bytes = new Uint8Array(await response.arrayBuffer());
        const total = totalFromContentRange(response.headers.get("Content-Range"));
        return response.status === 206 && total !== null
          ? { bytes, total, partial: true }
          : { bytes, total: bytes.byteLength, partial: false };
      }
      problem = await ApiProblem.fromResponse(response);
    } catch (error) {
      if (!isApiProblem(error)) {
        throw error;
      }
      problem = error;
    }
    const wait = RETRY_DELAYS_MS[attempt];
    if (!problem.isRetryable || wait === undefined) {
      throw problem;
    }
    await delay(wait);
  }
}

function notify(docUid: string): void {
  for (const listener of listeners.get(docUid) ?? []) {
    listener();
  }
}

function update(docUid: string, entry: Entry, generation: number, snapshot: PdfDocumentSnapshot): void {
  if (entries.get(docUid) !== entry || entry.generation !== generation) {
    return;
  }
  entry.snapshot = snapshot;
  notify(docUid);
}

async function open(docUid: string, entry: Entry, generation: number): Promise<void> {
  const fail = (error: unknown) => {
    update(docUid, entry, generation, { status: "error", doc: null, error });
  };
  try {
    const pdfjs = await loadPdfJs();
    const path = pdfPath(docUid);
    const first = await fetchRange(path, 0, RANGE_CHUNK_BYTES);
    const options = {
      ...PDFJS_ASSETS,
      rangeChunkSize: RANGE_CHUNK_BYTES,
      disableAutoFetch: true,
      disableStream: true,
    };
    let source: Parameters<PdfJs["getDocument"]>[0];
    if (first.partial && first.bytes.byteLength < first.total) {
      const transport = new pdfjs.PDFDataRangeTransport(first.total, first.bytes);
      transport.requestDataRange = (begin: number, end: number) => {
        fetchRange(path, begin, end).then((chunk) => {
          transport.onDataRange(begin, chunk.bytes);
        }, fail);
      };
      source = { ...options, range: transport };
    } else {
      source = { ...options, data: first.bytes };
    }
    const task = pdfjs.getDocument(source);
    entry.destroy = () => {
      void task.destroy();
    };
    const doc = await task.promise;
    update(docUid, entry, generation, { status: "ready", doc, error: null });
  } catch (error) {
    fail(error);
  }
}

function start(docUid: string, entry: Entry): void {
  generations += 1;
  entry.generation = generations;
  entry.snapshot = LOADING;
  entry.destroy = () => undefined;
  void open(docUid, entry, entry.generation);
}

function evictIdle(): void {
  for (const [docUid, entry] of entries) {
    if (entries.size <= MAX_OPEN_DOCUMENTS) {
      return;
    }
    if (entry.refs === 0) {
      drop(docUid, entry);
    }
  }
}

function drop(docUid: string, entry: Entry): void {
  if (entry.timer !== null) {
    clearTimeout(entry.timer);
  }
  entry.destroy();
  entries.delete(docUid);
}

export function acquirePdfDocument(docUid: string): void {
  let entry = entries.get(docUid);
  if (entry === undefined) {
    entry = { snapshot: LOADING, refs: 0, timer: null, destroy: () => undefined, generation: 0 };
    entries.set(docUid, entry);
    start(docUid, entry);
    evictIdle();
  }
  entry.refs += 1;
  if (entry.timer !== null) {
    clearTimeout(entry.timer);
    entry.timer = null;
  }
}

export function releasePdfDocument(docUid: string): void {
  const entry = entries.get(docUid);
  if (entry === undefined) {
    return;
  }
  entry.refs = Math.max(0, entry.refs - 1);
  if (entry.refs === 0) {
    entry.timer = setTimeout(() => {
      if (entries.get(docUid) === entry && entry.refs === 0) {
        drop(docUid, entry);
      }
    }, RELEASE_AFTER_MS);
  }
}

export function retryPdfDocument(docUid: string): void {
  const entry = entries.get(docUid);
  if (entry === undefined) {
    return;
  }
  entry.destroy();
  start(docUid, entry);
  notify(docUid);
}

function snapshotOf(docUid: string): PdfDocumentSnapshot {
  return entries.get(docUid)?.snapshot ?? LOADING;
}

function subscribe(docUid: string, listener: () => void): () => void {
  let set = listeners.get(docUid);
  if (set === undefined) {
    set = new Set();
    listeners.set(docUid, set);
  }
  set.add(listener);
  return () => {
    set.delete(listener);
    if (set.size === 0) {
      listeners.delete(docUid);
    }
  };
}

export function usePdfDocument(docUid: string): PdfDocumentState {
  useEffect(() => {
    acquirePdfDocument(docUid);
    return () => {
      releasePdfDocument(docUid);
    };
  }, [docUid]);
  const subscribeToDocument = useCallback((listener: () => void) => subscribe(docUid, listener), [docUid]);
  const snapshot = useSyncExternalStore(subscribeToDocument, () => snapshotOf(docUid));
  const retry = useCallback(() => {
    retryPdfDocument(docUid);
  }, [docUid]);
  return { ...snapshot, retry };
}
